import { useState, type ReactNode } from 'react';
import { PageHeader, SidePanel, Button } from '@indipia/ui-core';

// UI 표준 갤러리 — PC 레이아웃 절 (D-07 §21 · §12 · §7). design-system.html #sec-layout 과 같은 내용.
// 막대·목업은 실제 --layout-* 토큰을 참조하므로 토큰이 바뀌면 함께 움직인다.

const WIDTHS = [
  { key: 'sm', px: 480, use: '좁은 폼 · 인증' },
  { key: 'md', px: 720, use: '문서형 본문' },
  { key: 'lg', px: 1060, use: '본문 + 보조 컬럼 (기본값)' },
  { key: 'xl', px: 1440, use: '광폭 대시보드 (§7.2 Desktop 최대)' },
] as const;

const bar = 'rounded bg-border';
const accent = 'rounded bg-group';
const subtle = 'rounded bg-group-subtle';

// §21.5 유형별 골격 — 갤러리 전용 와이어프레임
const TYPES: { id: string; name: string; scroll: string; width: string; wire: ReactNode }[] = [
  { id: 'L1', name: '중앙 문서 컬럼', scroll: 'doc', width: 'md', wire: <div className={`${bar} mx-auto h-full w-3/5`} /> },
  {
    id: 'L2', name: '광폭 대시보드', scroll: 'doc', width: 'xl',
    wire: <div className="grid h-full grid-cols-2 gap-1">{[0, 1, 2, 3].map((i) => <div key={i} className={bar} />)}</div>,
  },
  {
    id: 'L3', name: '목록 + 우측패널', scroll: 'fixed', width: 'md · 308',
    wire: (
      <div className="flex h-full gap-1">
        <div className="flex flex-1 flex-col gap-1"><div className={`${subtle} h-2`} /><div className={`${bar} flex-1`} /></div>
        <div className={`${accent} w-6`} />
      </div>
    ),
  },
  { id: 'L4', name: '풀높이 에디터', scroll: 'fixed', width: '—', wire: <div className="flex h-full flex-col gap-1"><div className={`${subtle} h-2`} /><div className={`${bar} flex-1`} /></div> },
  {
    id: 'L5', name: '툴바 + 캔버스', scroll: 'fixed', width: 'full',
    wire: (
      <div className="flex h-full flex-col gap-1">
        <div className={`${subtle} h-2`} />
        <div className={`${bar} flex-1 opacity-60`} style={{ backgroundImage: 'repeating-linear-gradient(45deg, var(--shell-border) 0 4px, transparent 4px 8px)' }} />
      </div>
    ),
  },
  {
    id: 'L6', name: '스텝 위저드', scroll: 'fixed', width: 'md',
    wire: <div className="flex h-full flex-col gap-1"><div className={`${subtle} mx-auto h-2 w-3/4`} /><div className={`${bar} mx-auto w-3/5 flex-1`} /><div className={`${bar} h-2`} /></div>,
  },
  { id: 'L7', name: '본문 + 보조 컬럼', scroll: 'doc', width: 'lg', wire: <div className="flex h-full gap-1"><div className={`${bar} flex-1`} /><div className={`${accent} w-6`} /></div> },
  { id: 'L8', name: '중앙 상태', scroll: 'fixed', width: '—', wire: <div className="flex h-full items-center justify-center"><div className={`${bar} h-6 w-1/3`} /></div> },
  { id: 'L9', name: '인증 2분할', scroll: 'Shell 밖', width: '—', wire: <div className="grid h-full grid-cols-2 gap-1"><div className={accent} /><div className={bar} /></div> },
  {
    id: 'L10', name: '히어로 웰컴', scroll: 'Shell 밖', width: '—',
    wire: <div className="flex h-full flex-col gap-1"><div className={`${subtle} h-2`} /><div className={`${accent} flex-1`} /><div className={`${bar} mx-auto h-2 w-1/3`} /></div>,
  },
];

function Sub({ children }: { children: ReactNode }) {
  return <h3 className="mb-2 mt-8 text-base font-bold">{children}</h3>;
}
function Note({ children }: { children: ReactNode }) {
  return <p className="mb-3 text-sm text-text-secondary">{children}</p>;
}
const box = 'rounded-xxl border border-border bg-surface-raised p-4';
const code = 'rounded bg-surface px-1 font-mono text-xs';

export function LayoutSection() {
  const [open, setOpen] = useState(true);
  return (
    <section id="sec-layout" className="mb-10">
      <h2 className="mb-4 border-b border-border pb-2 text-lg font-bold text-point">
        Layout · Grid · Breakpoints
        <span className="ml-2 text-xs font-normal text-text-secondary">D-07 §21 · §12 · §7</span>
      </h2>

      <div className="mb-4 rounded-xxl border border-group bg-group-subtle p-4 text-sm text-group-onSubtle">
        <b>화면폭 정하기</b> — 페이지 폭은 <code className={code}>{'<Page width="sm|md|lg|xl|full">'}</code> 로만 정합니다.
        기획·시안에 화면폭 명시가 없으면 <b>lg(1060)</b> 가 기본이며, 콘텐츠에 맞게 다른 단계를 고를 수 있습니다.
        단계를 정하기 어려우면 아래 <b>§21.5 PC 레이아웃 10유형</b> 에서 가장 닮은 유형의 폭을 쓰세요.
      </div>

      <div className="flex flex-wrap items-start gap-4">
        <table className={`${box} text-sm`}>
          <thead><tr className="text-left text-text-secondary"><th className="pr-6">레이아웃</th><th>값</th></tr></thead>
          <tbody className="font-mono">
            <tr><td className="pr-6 font-sans">Page Padding (M/T/D)</td><td>16 / 24 / 32</td></tr>
            <tr><td className="pr-6 font-sans">Section Gap</td><td>32</td></tr>
            <tr><td className="pr-6 font-sans">Card Gap</td><td>24</td></tr>
          </tbody>
        </table>
        <table className={`${box} text-sm`}>
          <thead><tr className="text-left text-text-secondary"><th className="pr-6">Device</th><th className="pr-6">Col</th><th className="pr-6">Margin</th><th>Gutter</th></tr></thead>
          <tbody className="font-mono">
            <tr><td className="pr-6 font-sans">Mobile</td><td>4</td><td>16</td><td>16</td></tr>
            <tr><td className="pr-6 font-sans">Tablet</td><td>8</td><td>24</td><td>24</td></tr>
            <tr><td className="pr-6 font-sans">Desktop</td><td>12</td><td>32</td><td>24</td></tr>
          </tbody>
        </table>
      </div>

      <Sub>§21.2 PC Shell 구조</Sub>
      <Note>Shell + Contents 를 PC 에서 구현한 치수(웹 전용). LNB 펼침 240 · 서브패널 pin 시 +224.</Note>
      <div className="flex h-48 overflow-hidden rounded-xxl border border-border text-xs text-text-secondary">
        <div className="flex w-nav-rail shrink-0 flex-col items-center justify-center bg-lnb text-lnb-muted">rail<b className="font-mono text-lnb-fg">56</b></div>
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-topbar shrink-0 items-center justify-center gap-2 border-b border-border bg-surface-raised">TopBar<b className="font-mono text-group">56</b></div>
          <div className="flex-1 bg-surface p-3 text-center">
            Contents <b className="font-mono text-group">p-page 32</b>
            <div className="mx-auto mt-2 w-3/4 rounded-lg border border-dashed border-group py-2 text-group">중앙 컬럼 = §21.3</div>
          </div>
        </div>
        <div className="flex w-24 shrink-0 flex-col items-center justify-center border-l border-border bg-surface-raised text-center">Side<br />Panel<b className="font-mono text-group">308</b></div>
      </div>

      <Sub>§21.3 컨테이너 폭 스케일</Sub>
      <Note>페이지 중앙 컬럼의 최대폭. 막대는 실제 <code className={code}>--layout-container-*</code> 치수 그대로이며, 화면보다 넓은 단계는 가로로 스크롤해서 확인합니다.</Note>
      <div className="overflow-x-auto pb-1">
        {WIDTHS.map((w) => (
          <div
            key={w.key}
            className={`mb-2 flex h-9 items-center gap-3 rounded-lg px-3 text-sm ${w.key === 'lg' ? 'bg-group text-group-onPrimary' : 'bg-group-subtle text-group-onSubtle'}`}
            style={{ width: `var(--layout-container-${w.key})` }}
          >
            <span className="font-bold">{w.key}</span>
            <b className="font-mono">{w.px}</b>
            <span className="truncate opacity-80">{w.use}</span>
            <code className="ml-auto truncate font-mono text-xs">{`<Page width="${w.key}">`}</code>
          </div>
        ))}
      </div>
      <Note>
        <code className={code}>{'<Page width>'}</code> 가 정본입니다. <code className={code}>.lay-w-*</code>(폭 + 가운데 정렬)·
        <code className={code}>max-w-page-*</code>(폭만, 가운데 정렬 없음)는 <code className={code}>{'<Page>'}</code> 안쪽 영역용 하위 표기입니다.
      </Note>

      <Sub>§21.4 스크롤 소유권 — 폭보다 먼저 정할 것</Sub>
      <Note>규칙: 스크롤을 가진 쪽이 폭과 패딩을 가진다.</Note>
      <div className="flex flex-wrap gap-4">
        {[
          { tag: 'doc · 문서형', fixed: false, desc: '스크롤 소유자 = Contents. 제목도 함께 올라간다.' },
          { tag: 'fixed · 앱형', fixed: true, desc: '스크롤 소유자 = 페이지 내부. 툴바는 고정된다.' },
        ].map((d) => (
          <div key={d.tag} className={`${box} min-w-0 flex-1 basis-72`}>
            <div className="mb-2 text-xs font-bold text-group">{d.tag}</div>
            <div className={`flex h-40 flex-col rounded-lg border border-border bg-surface ${d.fixed ? 'overflow-hidden' : 'overflow-auto'}`}>
              {d.fixed && <div className={`${subtle} m-3 mb-0 shrink-0 px-3 py-1 text-xs font-bold text-group-onSubtle`}>툴바 · 필터바 (고정)</div>}
              <div className={`p-3 ${d.fixed ? 'min-h-0 flex-1 overflow-y-auto' : ''}`}>
                {!d.fixed && <div className={`${subtle} mb-2 px-3 py-1 text-xs font-bold text-group-onSubtle`}>페이지 제목</div>}
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="mb-2 h-8 rounded-md border border-border bg-surface-raised" />)}
              </div>
            </div>
            <p className="mt-2 text-xs text-text-secondary">{d.desc}</p>
          </div>
        ))}
      </div>

      <Sub>§21.5 PC 레이아웃 10유형</Sub>
      <Note>신규 PC 화면은 아래 중 하나를 고릅니다. L1~L8 은 Shell 내부(프리미티브 조립), L9·L10 은 Shell 밖. 적용 제외: 랜딩 · 약관 · 외부공유 수신자 뷰.</Note>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(var(--spacing-13),1fr))] gap-3">
        {TYPES.map((t) => (
          <div key={t.id}>
            <div className="h-20 rounded-lg border border-border bg-surface p-1">{t.wire}</div>
            <div className="mt-2 text-xs font-semibold">{t.id} {t.name}</div>
            <div className="text-xs text-text-secondary"><b className="text-group">{t.scroll}</b> · {t.width}</div>
          </div>
        ))}
      </div>

      <Sub>§21 프리미티브</Sub>
      <Note>ui-core <code className={code}>Page</code> · <code className={code}>PageScroll</code> · <code className={code}>PageHeader</code> · <code className={code}>SidePanel</code> (D-09 §15.41~15.44).</Note>
      <div className={box}>
        <PageHeader
          title="페이지 헤더"
          subtitle="PageHeader — 좌 타이틀(+서브) / 우 액션 슬롯"
          onBack={() => {}}
          extra={<><Button variant="secondary" size="sm">보조</Button><Button size="sm">주 액션</Button></>}
        />
        <div className="mt-3 flex h-48 overflow-hidden rounded-lg border border-border">
          <div className="flex flex-1 items-center justify-center bg-surface text-sm text-text-secondary">본문 영역</div>
          <SidePanel open={open} onToggle={() => setOpen((v) => !v)} title="SidePanel">
            <div className="p-3 text-sm text-text-secondary">308px, 닫히면 폭 0</div>
          </SidePanel>
        </div>
      </div>
    </section>
  );
}
